"use client";

import { AnimatePresence, motion } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "@/hooks/useTranslation";
import { useIsMobile } from "@/hooks/useIsMobile";
import GradientButton from "./GradientButton";

interface PhoneConfirmationModalProps {
	isOpen: boolean;
	name: string;
	countryFlag: string;
	countryName: string;
	dialCode: string;
	phoneNumber: string;
	isSubmitting: boolean;
	onClose: () => void;
	onConfirm: () => void;
}

function formatPhoneNumber(phoneNumber: string) {
	const digitsOnly = phoneNumber.replace(/[^\d]/g, "");
	return digitsOnly.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
}

export default function PhoneConfirmationModal({
	isOpen,
	name,
	countryFlag,
	countryName,
	dialCode,
	phoneNumber,
	isSubmitting,
	onClose,
	onConfirm,
}: PhoneConfirmationModalProps) {
	const { t } = useTranslation();
	const isMobile = useIsMobile();
	const formattedPhoneNumber = formatPhoneNumber(phoneNumber);

	const handleClose = () => {
		if (!isSubmitting) {
			onClose();
		}
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={isMobile ? {} : { opacity: 0 }}
					animate={isMobile ? {} : { opacity: 1 }}
					exit={isMobile ? {} : { opacity: 0 }}
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
					onClick={handleClose}
				>
					<motion.div
						initial={isMobile ? {} : { scale: 0.96, opacity: 0 }}
						animate={isMobile ? {} : { scale: 1, opacity: 1 }}
						exit={isMobile ? {} : { scale: 0.96, opacity: 0 }}
						onClick={(event) => event.stopPropagation()}
						className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
					>
						<div className="flex items-center justify-between border-b border-gray-200 p-6">
							<div>
								<p className="text-lg font-semibold text-gray-900">
									{t("contact.form.confirmation.greeting", { name })}
								</p>
								<h2 className="text-2xl font-bold text-gray-900">
									{t("contact.form.confirmation.title")}
								</h2>
								<p className="mt-1 text-sm text-gray-600">
									{t("contact.form.confirmation.description")}
								</p>
							</div>
							<button
								type="button"
								onClick={handleClose}
								disabled={isSubmitting}
								className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>

						<div className="space-y-4 p-6">
							<div className="rounded-2xl bg-slate-50 p-4">
								<div className="border-b border-slate-200 pb-3">
									<p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
										{t("contact.form.confirmation.country")}
									</p>
									<p className="mt-1 text-lg font-semibold text-slate-900">
										{countryName}
									</p>
								</div>
								<div className="flex gap-4 pt-3">
									<div className="flex-1">
										<p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
											{t("contact.form.confirmation.dialCode")}
										</p>
										<p className="mt-1 flex items-center gap-2 text-base font-semibold text-slate-900">
											<span className="text-lg">{countryFlag}</span>
											<span>{dialCode}</span>
										</p>
									</div>
									<div className="flex-[1.2]">
										<p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
											{t("contact.form.confirmation.phone")}
										</p>
										<p className="mt-1 text-base font-semibold text-slate-900">
											{formattedPhoneNumber}
										</p>
									</div>
								</div>
							</div>
							<p className="text-sm leading-relaxed text-slate-600">
								{t("contact.form.confirmation.contactNotice")}
							</p>
						</div>

						<div className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 p-6 sm:flex-row sm:justify-end">
							<button
								type="button"
								onClick={handleClose}
								disabled={isSubmitting}
								className="rounded-full px-6 py-3 font-semibold text-gray-600 transition-colors hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{t("contact.form.confirmation.edit")}
							</button>
							<GradientButton
								type="button"
								onClick={onConfirm}
								disabled={isSubmitting}
								className="sm:min-w-[220px]"
							>
								{isSubmitting
									? t("contact.form.sending")
									: t("contact.form.confirmation.confirm")}
							</GradientButton>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
